/**
 * Turn a deep link's plain params into the pages' own filter chips.
 *
 * "Open in Traces" under a chat answer used to land on an unfiltered page.
 * The adapters had always attached the filters the answer was built from —
 * `?agent_name=chat&has_error=true` — but no page read them: they only knew
 * `globalFilters`, a JSON array of query-builder chips. So the link opened
 * the right page showing everything, which reads as "it took me somewhere
 * random", and the rows you had just been looking at were nowhere in it.
 *
 * These are the fields each list actually filters on, so a param that cannot
 * be honoured is dropped rather than silently ignored.
 */
const SPAN_FIELDS = new Set([
  'name', 'kind', 'status', 'tool_name', 'agent_name', 'distinct_id',
  'conversation_id', 'model', 'provider', 'run_id', 'parent_span_id',
]);

const RUN_FIELDS = new Set([
  'agent_name', 'status', 'distinct_id', 'conversation_id', 'parent_run_id',
  'feedback_satisfaction',
]);

const FIELDS = { spans: SPAN_FIELDS, runs: RUN_FIELDS };

/**
 * @param {URLSearchParams} searchParams
 * @param {'spans'|'runs'} kind which list is reading them
 * @returns {Array<{field:string, operator:string, value:string, source:string}>}
 */
export function filtersFromParams(searchParams, kind) {
  const allowed = FIELDS[kind] || SPAN_FIELDS;
  // BOTH lists route a chip by its category, and both drop a chip that has
  // none — silently. That is what made these links look broken: the link
  // arrived carrying has_error=true, the page showed "Add filter 1", and the
  // request came back with all 854 runs in it.
  //
  // 'global' means a RUN field on either list — on the spans list it filters
  // spans by their parent run. A span's own columns need any other category,
  // and 'span_properties' is the one the picker uses.
  const category = kind === 'runs' ? 'global' : 'span_properties';
  const chip = (field, operator, value, dataType) => ({
    field, operator, value, source: 'fixed', dataType, category,
  });
  const out = [];
  for (const field of allowed) {
    const value = searchParams.get(field);
    if (value != null && value !== '') out.push(chip(field, 'is', value, 'string'));
  }
  // `has_error=true` is the answer's "failed only". A run counts its errors;
  // a span either is one or is not.
  if (searchParams.get('has_error') === 'true') {
    out.push(kind === 'runs'
      ? chip('error_count', 'greater_than', '0', 'number')
      : chip('status', 'is', 'error', 'string'));
  }
  return out;
}

export { SPAN_FIELDS, RUN_FIELDS };
